// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import queryString from 'query-string';
import { withRouter } from 'react-router';
import type { Location, RouterHistory } from 'react-router';
import Box from 'components/Box';
import Hide from 'components/Hide';
import GradFairsLinks from 'components/GradFairsLinks';
import Layout from 'layouts/Layout';
import AmpContext from 'utils/AmpContext';
import type { HeroSearchPartialRender, Layout as CmsLayout } from 'api/cms';
import { setScrollRestorationManual } from 'utils/browser';
import type { State as MainSearch } from 'redux/main/reducers/mainSearch';
import { isDefined } from 'utils/functional';
import { getParam } from 'utils/url';
import { listingSearchHomePath } from 'utils/searchHomePages';
import { isEnabled as daysEnabled } from 'utils/idealistdayevents';
import type { State as PostSearch } from '../redux/main/reducers/postSearch';
import { breakpoints, gutterWidth, maxWidth, transition } from '../theme';
import {
  initializeSearch as initializeMainSearchRaw,
  initializeLandingPageSearch as initializeLandingPageSearchRaw,
  uninitializeSearch as uninitializeMainSearchRaw,
  setSearchFilter as setSearchFilterRaw,
  setUserInitiatedSearch as setUserInitiatedSearchRaw
} from '../redux/main/actions/mainSearch';
import {
  initializeSearch as initializePostSearchRaw,
  reinitializeSearch as reinitializePostSearchRaw,
  uninitializeSearch as uninitializePostSearchRaw
} from '../redux/main/actions/postSearch';

const { useEffect, useContext, useState } = React;

const ResultsContainer = styled(Box)`
  @media (max-width: ${maxWidth[3] - 1}px) {
    margin-bottom: -54px;
  }
`;

const Results = styled(Box)`
  ${props =>
    props.$hideContainer &&
    `
  &::before {
    opacity: 0;
  }
`}
  @media (min-width: ${breakpoints[2]}) {
    &::before {
      content: '';
      position: absolute;
      top: -${gutterWidth / 2}px;
      left: 0;
      width: 100%;
      height: calc(100% + ${gutterWidth / 2}px);
      background: white;
      transition: all ${transition};
    }
  }
  @media (min-width: ${maxWidth[3]}px) {
    &::before {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      border-radius: 12px;
    }
  }
`;

const initializeMainSearchEffect = ({
  initializeLandingPageSearch,
  initializeMainSearch,
  uninitializeMainSearch,
  setUserInitiatedSearch,
  search,
  pathname,
  initialized,
  landingPageInitialized,
  defaultSearchType
}: {
  initializeLandingPageSearch: Function,
  initializeMainSearch: Function,
  uninitializeMainSearch: Function,
  setUserInitiatedSearch: Function,
  search: string,
  pathname: string,
  initialized: boolean,
  landingPageInitialized: boolean,
  defaultSearchType: string
}) => {
  // May not be a main search: see staff search when navigating to an item
  if (!initialized) {
    if (!landingPageInitialized && window.Idealist.pageData.landingSearch) {
      const landingSearch = window.Idealist.pageData.landingSearch;

      initializeLandingPageSearch({
        location: landingSearch.location,
        landingPagePath: pathname,
        filters: landingSearch.filters,
        autosaveSearch: landingSearch.autosaveSearch === 'True'
      });
      setUserInitiatedSearch();
    } else {
      initializeMainSearch({
        queryParams: queryString.parse(search),
        defaultSearchType
      });
    }
    return function cleanup() {
      if (initialized) {
        uninitializeMainSearch();
      }
    };
  }
};

const SearchPageContainer = ({
  toolbar,
  staffTools,
  hero,
  results,
  content,
  layout,
  subsiteId,
  url,
  postSearchToolbar,
  searchMode = false,
  setSearchMode,
  navSection,
  isPostSearch,
  onlyResults,
  onSearch,
  defaultSearchType,
  darkMode,
  showListingCounts,
  hideFooterDivider,
  searches,
  searchActions,
  location,
  history,
  searchLocation,
  exposeSiteNav
}: {
  toolbar?: React.Node,
  staffTools?: ?React.Node,
  hero?: HeroSearchPartialRender,
  results: React.Node,
  content?: React.Node,
  layout: CmsLayout,
  subsiteId?: string,
  url?: Object,
  postSearchToolbar?: React.Node,
  searchMode?: boolean,
  setSearchMode: Function,
  navSection?: 'MAIN' | 'STAFF',
  isPostSearch: boolean,
  onlyResults?: boolean,
  onSearch?: Function,
  defaultSearchType: ListingType,
  showListingCounts?: boolean,
  hideFooterDivider?: boolean,
  darkMode?: boolean,
  initializeLandingPageSearch: Function,
  location: Location,
  history: RouterHistory,
  searchActions: {
    initializeMainSearch: Function,
    initializeLandingPageSearch: Function,
    uninitializeMainSearch: Function,
    initializePostSearch: Function,
    initializeVolopSearch: Function,
    setSearchFilter: (string, string) => void,
    setUserInitiatedSearch: Function,
    reinitializePostSearch: Function,
    uninitializePostSearch: Function
  },
  searches: {
    mainSearch: ?MainSearch,
    postSearch: ?PostSearch
  },
  searchLocation: SearchLocation,
  exposeSiteNav: boolean
}) => {
  const ampEnabled = useContext(AmpContext);

  const { initialized = false, landingPageInitialized = false } =
    searches.mainSearch || {};
  const { initialized: postSearchInitialized = false } =
    searches.postSearch || {};
  const {
    initializeMainSearch,
    initializePostSearch,
    initializeLandingPageSearch,
    setUserInitiatedSearch,
    uninitializeMainSearch,
    reinitializePostSearch,
    uninitializePostSearch
  } = searchActions;

  const [initialSubsiteId] = useState(subsiteId);

  useEffect(
    () => {
      if (!isPostSearch && !initialized) {
        return initializeMainSearchEffect({
          initialized,
          landingPageInitialized,
          initializeMainSearch,
          initializeLandingPageSearch,
          setUserInitiatedSearch,
          uninitializeMainSearch,
          pathname: location.pathname,
          search: location.search,
          defaultSearchType
        });
      }
    },
    [
      initialized,
      landingPageInitialized,
      isPostSearch,
      searchActions,
      location.pathname,
      location.search,
      defaultSearchType,
      searchMode,
      initializeMainSearch,
      initializeLandingPageSearch,
      setUserInitiatedSearch,
      uninitializeMainSearch
    ]
  );

  useEffect(() => setScrollRestorationManual(), []);

  // For disabling Idealist Day Events
  const replace = history.replace;
  useEffect(
    () => {
      if (!daysEnabled() && defaultSearchType === 'IDEALIST_DAY_EVENT') {
        replace(listingSearchHomePath('GROUP'));
      }
    },
    [defaultSearchType, replace]
  );

  useEffect(
    () =>
      function cleanup() {
        if (
          typeof setUserInitiatedSearch === 'function' &&
          !getParam(window.location, 'q') &&
          !getParam(window.location, 'searchMode')
        ) {
          setUserInitiatedSearch(false);
          setSearchMode(false);
        }
      },
    [location, setSearchMode, setUserInitiatedSearch]
  );

  useEffect(
    () => {
      if (isPostSearch && subsiteId && !postSearchInitialized) {
        initializePostSearch({
          queryParams: queryString.parse(location.search),
          subsiteId
        });
      }
    },
    [
      initializePostSearch,
      isPostSearch,
      location.search,
      postSearchInitialized,
      subsiteId
    ]
  );

  useEffect(
    () => {
      if (isPostSearch && subsiteId !== initialSubsiteId) {
        reinitializePostSearch({
          subsiteId
        });
      }
      return () => {
        if (postSearchInitialized) {
          uninitializePostSearch();
        }
      };
    },
    [
      initialSubsiteId,
      isPostSearch,
      postSearchInitialized,
      reinitializePostSearch,
      subsiteId,
      uninitializePostSearch
    ]
  );

  const hasSearchHero = isDefined(hero);

  return (
    <Layout
      url={url}
      toolbar={toolbar}
      staffTools={staffTools}
      hero={hero}
      darkMode={darkMode}
      defaultWithSearchHero={hasSearchHero && !searchMode}
      searchHeroHasCta={
        hasSearchHero && Object.prototype.hasOwnProperty.call(hero, 'cta')
      }
      navSection={navSection}
      searchMode={searchMode}
      setUserInitiatedSearch={setUserInitiatedSearch}
      onSearch={onSearch}
      defaultSearchType={defaultSearchType}
      postSearchToolbar={postSearchToolbar}
      showListingCounts={showListingCounts}
      hideFooterDivider={hideFooterDivider}
      layout={layout}
      exposeSiteNav={exposeSiteNav}
    >
      <>
        {searchMode && !ampEnabled ? (
          <ResultsContainer mt="-1px" pt="1px">
            <Results
              $hideContainer={searchMode || onlyResults || isPostSearch}
              position="relative"
              maxWidth={`${maxWidth[3]}px`}
              mx="auto"
              mt={[
                gutterWidth / 2,
                null,
                null,
                isPostSearch
                  ? 0
                  : showListingCounts
                  ? gutterWidth - 100
                  : hasSearchHero && !searchMode
                  ? -gutterWidth / 2
                  : undefined
              ]}
              pt={showListingCounts ? undefined : gutterWidth / 2}
              pb={gutterWidth}
              id="results"
              data-qa-id="search-results-body"
            >
              <Box position="relative">{results}</Box>
            </Results>

            <Hide lg xl px={gutterWidth / 2} pb={gutterWidth}>
              <GradFairsLinks searchLocation={searchLocation} />
            </Hide>
          </ResultsContainer>
        ) : (
          content
        )}
      </>
    </Layout>
  );
};

const mapStateToProps = ({
  mainSearch,
  postSearch,
  currentUser: { searchLocation }
}) => ({
  searches: { mainSearch, postSearch },
  searchLocation
});

export default withRouter(
  connect(
    mapStateToProps,
    dispatch => ({
      searchActions: bindActionCreators(
        {
          initializeMainSearch: initializeMainSearchRaw,
          initializeLandingPageSearch: initializeLandingPageSearchRaw,
          uninitializeMainSearch: uninitializeMainSearchRaw,
          initializePostSearch: initializePostSearchRaw,
          setUserInitiatedSearch: setUserInitiatedSearchRaw,
          setSearchFilter: setSearchFilterRaw,
          reinitializePostSearch: reinitializePostSearchRaw,
          uninitializePostSearch: uninitializePostSearchRaw
        },
        dispatch
      )
    })
  )(SearchPageContainer)
);
